import { ChangeEvent, CSSProperties, useState } from "react"

import { CellProps, Col } from "components/Rable/types"

import { ClickAwayListener } from "@material-ui/core"
import { HexColorPicker } from "react-colorful"
import Input from "ui/inputs/Input"
import { MediaTypeMbaConfig } from "models/mba"
import { css, cx } from "@emotion/css"
import get from "lodash/get"
import { set } from "lodash/fp"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

function ReadOnlyCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { row, name } = props
  const initialValue = get(row, name, "")

  return <div>{t(`mba.media-types-settings.${initialValue}`)}</div>
}

function ColorCell<T extends Record<string, any>>(
  props: CellProps<T> & { data: Partial<MediaTypeMbaConfig> },
) {
  const { hasAuthority } = useAuthenticated()
  const { data, rowId, onUpdate } = props
  const [updatedData, setUpdatedData] = useState(data)
  const [openPicker, setOpenPicker] = useState(false)
  const value = get(updatedData, `colorConfig.${rowId}`)
  const oldValue = get(data, `colorConfig.${rowId}`)
  const isAllowed = hasAuthority(["AUTH_EDIT_MBA_CONFIG"])

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = evt.target.value
    const updatedData = set(`colorConfig.${rowId}`, updatedValue, data)
    setUpdatedData(updatedData)
  }

  const handlePickerChange = (color: string) => {
    const updatedData = set(`colorConfig.${rowId}`, color, data)
    setUpdatedData(updatedData)
  }

  const handleBlur = () => {
    oldValue !== value && onUpdate(rowId, updatedData)
  }

  const handleOpenPicker = () => isAllowed && setOpenPicker(true)
  const handleClosePicker = () => {
    setOpenPicker(false)
    handleBlur()
  }

  const iconStyle = {
    backgroundColor: `${value}`,
    minWidth: "var(--spacing-s)",
    height: "var(--spacing-s)",
    borderRadius: "50%",
    border: value === "#ffffff" ? "1px solid" : "none",
  } as CSSProperties

  return (
    <div className={styles.colorcell}>
      <Input
        type="text"
        id={rowId}
        className={cx(styles.input, { [styles.disabled]: !isAllowed })}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={!isAllowed}
      />
      <span style={iconStyle} onClick={handleOpenPicker} />
      {openPicker && (
        <ClickAwayListener
          mouseEvent="onMouseUp"
          onClickAway={handleClosePicker}
        >
          <div style={{ position: "absolute", right: 12 }}>
            <HexColorPicker
              className={styles.hexcolorpicker}
              color={value}
              onChange={handlePickerChange}
            />
          </div>
        </ClickAwayListener>
      )}
    </div>
  )
}

export const columns = (
  data: Partial<MediaTypeMbaConfig>[],
): Col<Record<string, string>>[] => {
  const columns = [
    {
      id: "Element",
      name: "id",
      cell: ReadOnlyCell,
      width: "max-content",
      existsInCreate: true,
      required: true,
    },
  ]

  const dynamic = data.map((mtc) => ({
    id: mtc.name || "",
    name: mtc.name || "",
    cell: (props: CellProps<Record<string, string>>) => {
      return <ColorCell {...props} data={mtc} />
    },
    width: "max-content",
    existsInCreate: true,
    required: true,
  }))

  return [...columns, ...dynamic]
}

const styles = {
  colorcell: css`
    width: 80px;
    display: flex;
    align-items: center;
    position: relative;
  `,
  input: css`
    font-size: 13px;
    font-weight: inherit;
    border-width: 0;
  `,
  hexcolorpicker: css`
    position: absolute;
    top: 100%;
    z-index: 999;
  `,
  disabled: css`
    color: rgba(0, 0, 0, 0.38);
  `,
}
